import React, { useCallback, useState } from 'react';

import { useSearchParams, useRouter, usePathname } from 'next/navigation';

import { DrawerView, ExploreQueryParams } from '../../../components/Maps/types/types';
import { shallowRemoveBlankValues } from '../../../helpers/objects/shallowRemoveBlankValues';
import { objectToQueryString, queryStringToObject } from '../../../helpers/url/queryUtils';

import { getParamsFromQuery, getQueryFromParams } from './helpers';

interface ExploreContext {
  saveParamsInUrl: (params: Partial<ExploreQueryParams>, options?: { replace: boolean }) => void;
  exploreQueryParams: ExploreQueryParams;
  isDrawerInitialized: boolean;
  setIsDrawerInitialized: (newValue: boolean) => void;
  filterViewBackButtonPath: DrawerView;
  setFilterViewBackButtonPath: (newDrawerView: DrawerView) => void;
}

export const exploreContextInitialValue: ExploreContext = {
  saveParamsInUrl: () => {},
  exploreQueryParams: {
    latitude: undefined,
    longitude: undefined,
    zoom: undefined,
    search: undefined,
    drawerView: undefined,
    drawerViewId: undefined,
    mapStyle: undefined,
    catchFiltersSpecies: undefined,
    catchFiltersMonths: undefined,
    postViewerOpen: undefined,
    currentPostViewerId: undefined,
  },
  isDrawerInitialized: false,
  setIsDrawerInitialized: () => {},
  filterViewBackButtonPath: 'catches',
  setFilterViewBackButtonPath: () => {},
};

const ExploreContext = React.createContext<ExploreContext>(exploreContextInitialValue);

const ExploreProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const query = queryStringToObject(searchParams?.toString());

  const { replace: routerReplace, push: routerPush } = useRouter();
  const exploreQueryParams: ExploreQueryParams = getParamsFromQuery(query);
  const [isDrawerInitialized, setIsDrawerInitialized] = useState(false);
  const [filterViewBackButtonPath, setFilterViewBackButtonPath] = useState<DrawerView>('catches');

  /**
   * This function is used to update the URL with the new explore query params.
   * It uses the current query params and spreads in the new query params to create a new query string.
   * It then uses either push or replace from next/navigation to update the url.
   * ATTENTION: This function is not async anymore and cant be awaited. Using it multiple times in a row will
   * likely overwrite the previous call.
   */
  const saveParamsInUrl = useCallback(
    (
      newQueryParams: Partial<ExploreQueryParams>,
      { replace }: { replace?: boolean; skipIfSomeOtherUpdateIsExecuted?: boolean } = {},
    ) => {
      const method = replace ? routerReplace : routerPush;
      method(
        `${pathname}?${objectToQueryString(
          shallowRemoveBlankValues({
            ...query,
            ...getQueryFromParams(newQueryParams),
          }),
        )}`,
      );
    },
    [routerReplace, routerPush, pathname, query],
  );

  const value: ExploreContext = {
    exploreQueryParams,
    saveParamsInUrl,
    isDrawerInitialized,
    setIsDrawerInitialized,
    filterViewBackButtonPath,
    setFilterViewBackButtonPath,
  };

  return <ExploreContext.Provider value={value}>{children}</ExploreContext.Provider>;
};

const useExplore = (): ExploreContext => React.useContext(ExploreContext);

export { ExploreContext, ExploreProvider, useExplore };
